import { gql } from 'graphql-request'
import { queryClient, reportGqlError, request, useMutation } from '../../api'
import { BUSINESS_REFUND_SETTINGS_QUERY_KEY } from './queryKeys'
import { ServerBusinessRefundSettingsThresholdInsertInput } from '../../generated/graphql-types'

export const useUpdateBusinessRefundSettingsById = () => {
  return useMutation(
    async (dto: {
      // Refunded settings
      refundSettingId: number
      isActive: boolean
      // Subscription policy settings
      isRefundSubscriptionAllowed?: boolean
      // Gift card policy settings
      isRefundGiftCardAllowed?: boolean
      // Booking policy settings
      isRefundBookingAllowed?: boolean
      // Thresholds
      businessRefundSettingsThresholdsInsertInput: ServerBusinessRefundSettingsThresholdInsertInput[]
      businessRefundSettingsPolicyIdsToDeleteThresholds: number[]
    }) => {
      return request<{
        insertBusinessRefundSettingsThresholds: { affected_rows: number }
        deleteBusinessRefundSettingsThresholds: { affected_rows: number }
      }>(
        gql`
          mutation MyMutation(
            # Refunded settings
            $refundSettingId: Int!
            $isActive: Boolean!
            # Subscription policy settings
            $isRefundSubscriptionAllowed: Boolean
            $isRefundSubscriptionAllowedMutationEnabled: Boolean!
            # Gift card policy settings
            $isRefundGiftCardAllowed: Boolean
            $isRefundGiftCardAllowedMutationEnabled: Boolean!
            # Booking policy settings
            $isRefundBookingAllowed: Boolean
            $isRefundBookingAllowedMutationEnabled: Boolean!
            # Intervals
            $businessRefundSettingsThresholdsInsertInput: [businessRefundSettingsThreshold_insert_input!]!
            $businessRefundSettingsPolicyIdsToDeleteThresholds: [Int!]!
          ) {
            updateBusinessRefundSettingsById(
              pk_columns: { id: $refundSettingId }
              _set: { isActive: $isActive }
            ) {
              id
            }
            updateBusinessSubscriptionRefundSettingsPolicy: updateBusinessRefundSettingsPolicies(
              where: {
                businessRefundSettingsId: { _eq: $refundSettingId }
                type: { _eq: SUBSCRIPTION }
              }
              _set: { isRefundAllowed: $isRefundSubscriptionAllowed }
            ) @include(if: $isRefundSubscriptionAllowedMutationEnabled) {
              affected_rows
            }
            updateBusinessGiftCardRefundSettingsPolicy: updateBusinessRefundSettingsPolicies(
              where: {
                businessRefundSettingsId: { _eq: $refundSettingId }
                type: { _eq: GIFT_CARD }
              }
              _set: { isRefundAllowed: $isRefundGiftCardAllowed }
            ) @include(if: $isRefundGiftCardAllowedMutationEnabled) {
              affected_rows
            }

            updateBusinessBookingRefundSettingsPolicy: updateBusinessRefundSettingsPolicies(
              where: { businessRefundSettingsId: { _eq: $refundSettingId }, type: { _eq: BOOKING } }
              _set: { isRefundAllowed: $isRefundBookingAllowed }
            ) @include(if: $isRefundBookingAllowedMutationEnabled) {
              affected_rows
            }

            deleteBusinessRefundSettingsThresholds(
              where: {
                businessRefundSettingsPolicyId: {
                  _in: $businessRefundSettingsPolicyIdsToDeleteThresholds
                }
              }
            ) {
              affected_rows
            }

            insertBusinessRefundSettingsThresholds(
              objects: $businessRefundSettingsThresholdsInsertInput
            ) {
              affected_rows
            }
          }
        `,
        {
          ...dto,
          isRefundSubscriptionAllowedMutationEnabled:
            typeof dto.isRefundSubscriptionAllowed !== 'undefined',
          isRefundGiftCardAllowedMutationEnabled:
            typeof dto.isRefundGiftCardAllowed !== 'undefined',
          isRefundBookingAllowedMutationEnabled: typeof dto.isRefundBookingAllowed !== 'undefined',
        },
      )
    },
    {
      onError: reportGqlError,
      onSuccess: () => queryClient.invalidateQueries([BUSINESS_REFUND_SETTINGS_QUERY_KEY]),
    },
  )
}
